import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function SymbolsPage() {
  const [expandedArchetype, setExpandedArchetype] = useState<string | null>(null);
  const { data: topSymbols, isLoading: loadingSymbols } = useQuery({
    queryKey: ['top-symbols'],
    queryFn: () => api.symbols.getTop(50),
  });

  const { data: archetypes, isLoading: loadingArchetypes } = useQuery({
    queryKey: ['archetypes'],
    queryFn: () => api.symbols.getArchetypes(),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-12">
        <h1 className="text-3xl font-display font-bold text-white mb-2">Dream Symbols</h1>
        <p className="text-dream-300">
          Explore the symbols and archetypes that appear in dreams, with interpretations from
          Jung, Hillman, classical mythology, and Greek tradition.
        </p>
      </div>

      {/* Symbol Cloud */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">Popular Symbols</h2>
        {loadingSymbols ? (
          <div className="flex flex-wrap gap-2">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="h-8 w-20 bg-dream-800 rounded-full animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topSymbols?.map((symbol) => {
              // Size based on dream count
              const size = Math.min(Math.max(symbol.dreamCount / 5, 0.8), 2);
              return (
                <button
                  key={symbol.id}
                  className="px-3 py-1.5 bg-dream-800/50 hover:bg-dream-700 text-dream-200 hover:text-white rounded-full transition-colors"
                  style={{ fontSize: `${size}rem` }}
                >
                  {symbol.name}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Archetypes */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Jungian Archetypes</h2>
        {loadingArchetypes ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-6 w-1/2 bg-dream-700 rounded mb-2" />
                <div className="h-16 bg-dream-700 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {archetypes?.map((archetype) => {
              const isExpanded = expandedArchetype === archetype.id;
              return (
                <div key={archetype.id} className="card hover:border-dream-600 transition-colors">
                  <h3 className="text-lg font-semibold text-white mb-2">{archetype.name}</h3>
                  <p className={`text-dream-300 text-sm mb-3 ${isExpanded ? '' : 'line-clamp-3'}`}>
                    {archetype.description}
                  </p>
                  {isExpanded && archetype.meaning && (
                    <div className="mb-3 pt-3 border-t border-dream-800">
                      <h4 className="text-sm font-medium text-dream-400 mb-1">Meaning</h4>
                      <p className="text-dream-300 text-sm">{archetype.meaning}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-dream-400 text-xs">
                      Appears in {archetype.dreamCount} dreams
                    </span>
                    <button
                      className="text-dream-400 hover:text-white text-sm"
                      onClick={() => setExpandedArchetype(isExpanded ? null : archetype.id)}
                    >
                      {isExpanded ? 'Show less' : 'Learn more \u2192'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
